import {
  IconBasketDiscount,
  IconBox,
  IconCurrencyDollar,
  IconMoneybag,
} from '@tabler/icons-react'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Loading,
  MetricCard,
  ScalrBreadcrumb,
  ScalrCarousel,
  ScalrEmptyBox,
} from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { createTab, TabConfig } from '@/components/TabSkeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList } from '@/components/ui/tabs'

import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { toast, useSalesLensItemMetricCard, useTabAlerts } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxSalesLensFilter,
} from '@/store'
import { dateToString, decodeAndFormatPageId } from '@/utils/utils'

import { BoughtTogetherTab } from '@/screens/clarityLens/salesLens/BoughtTogetherTab'
import { DiscountTab } from '@/screens/clarityLens/salesLens/DiscountTab'
import { PaymentTypeTab } from '@/screens/clarityLens/salesLens/PaymentTypeTab'
import { ProfitabilityTab } from '@/screens/clarityLens/salesLens/ProfitabilityTab'
import { SalesTab } from '@/screens/clarityLens/salesLens/SalesTab'

const SalesLensContent = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [pageId] = useState<{ categoryName: string; itemName: string } | null>(
    () => {
      return decodeAndFormatPageId(id, 'sales-lens')
    }
  )

  const handleBack = () => {
    navigate('/clarity-lens/sales-lens')
  }

  const { tabAlertStatus, markTabAlertsAsRead } = useTabAlerts(
    pageId?.itemName || '',
    'Sales Lens'
  )

  const tabConfigs: TabConfig[] = [
    // {
    //   value: 'overview',
    //   title: t('clarityLens.salesLens.tabs.overview'),
    //   hasUnreadAlerts: tabAlertStatus['Overview']?.hasUnread,
    //   onTabClick: () => markTabAlertsAsRead('Overview'),
    //   content: (
    //     <>
    //       {pageId?.itemName ? (
    //         <OverviewTab itemName={pageId?.itemName} />
    //       ) : (
    //         <Loading isContainer />
    //       )}
    //     </>
    //   ),
    // },
    {
      value: 'sales',
      title: t('clarityLens.salesLens.tabs.sales'),
      hasUnreadAlerts: tabAlertStatus['Sales']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Sales'),
      content: (
        <>
          {pageId?.itemName ? (
            <SalesTab itemName={pageId?.itemName} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    {
      value: 'boughtTogether',
      title: t('clarityLens.salesLens.tabs.boughtTogether'),
      hasUnreadAlerts: tabAlertStatus['Bought Together']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Bought Together'),
      content: (
        <>
          {pageId?.itemName ? (
            <BoughtTogetherTab itemName={pageId?.itemName} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    {
      value: 'discounts',
      title: t('clarityLens.salesLens.tabs.discounts'),
      hasUnreadAlerts: tabAlertStatus['Discounts']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Discounts'),
      content: (
        <>
          {pageId?.itemName ? (
            <DiscountTab itemName={pageId?.itemName} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    {
      value: 'paymentType',
      title: t('clarityLens.salesLens.tabs.paymentType'),
      hasUnreadAlerts: tabAlertStatus['Payment Type']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Payment Type'),
      content: (
        <>
          {pageId?.itemName ? (
            <PaymentTypeTab itemName={pageId?.itemName} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
    {
      value: 'profitability',
      title: t('clarityLens.salesLens.tabs.profitability'),
      hasUnreadAlerts: tabAlertStatus['Profitability']?.hasUnread,
      onTabClick: () => markTabAlertsAsRead('Profitability'),
      content: (
        <>
          {pageId?.itemName ? (
            <ProfitabilityTab itemName={pageId?.itemName} />
          ) : (
            <Loading isContainer />
          )}
        </>
      ),
    },
  ]

  const tabs = tabConfigs.map((config) => createTab(config))

  const { selectedBranch, date, temperatureFilter, sizeFilter } =
    useReduxSalesLensFilter()
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('sales-lens-item')
  )

  const {
    salesLensItemMetricCardData,
    trendData,
    loading: salesLensItemMetricCardLoading,
    error: salesLensItemMetricCardError,
    refetch: salesLensItemMetricCardRefetch,
  } = useSalesLensItemMetricCard(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    pageId?.itemName || '',
    sizeFilter,
    temperatureFilter
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing discounts items...')
      salesLensItemMetricCardRefetch()
    }
  }, [refreshTrigger, salesLensItemMetricCardRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (salesLensItemMetricCardError) {
      toast({
        title: t('common.errors.serverError'),
        description: salesLensItemMetricCardError?.message,
        variant: 'destructive',
      })
    }
  }, [salesLensItemMetricCardError])

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-row justify-between mb-4">
          <div className="flex items-center">
            <Button onClick={handleBack} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('clarityLens.salesLens.back')}
            </Button>
            <CardTitle>{pageId?.itemName}</CardTitle>
          </div>
        </div>
        <ScalrBreadcrumb
          breadcrumbItems={[
            { label: t('clarityLens.navigation.clarityLens') },
            {
              label: t('clarityLens.navigation.salesLens'),
              href: '/clarity-lens/sales-lens',
            },
          ]}
          title={pageId?.categoryName}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        <FilterBar pageId="sales-lens-item" />

        {salesLensItemMetricCardError ? (
          <ScalrEmptyBox />
        ) : (
          <div>
            <div className="space-y-3 mb-10">
              <ScalrCarousel fitMoreCard>
                <MetricCard
                  title={t('clarityLens.salesLens.metrics.dailySales')}
                  type="money"
                  content={salesLensItemMetricCardData?.netSalesAmount || 0}
                  mtdCurrContent={salesLensItemMetricCardData?.netSalesAmount}
                  ytdCurrContent={salesLensItemMetricCardData?.netSalesAmount}
                  mtdPrevContent={trendData?.prevMetricsMonthly?.netSalesAmount}
                  ytdPrevContent={trendData?.prevMetricsYearly?.netSalesAmount}
                  icon={<IconMoneybag color="white" size={20} />}
                  color={metricCardColors.blue500}
                  loading={salesLensItemMetricCardLoading}
                />
                <MetricCard
                  title={t('clarityLens.salesLens.metrics.percentOfTotalSales')}
                  type="percentage"
                  content={salesLensItemMetricCardData?.netSalesPercentage || 0}
                  mtdCurrContent={
                    salesLensItemMetricCardData?.netSalesPercentage
                  }
                  ytdCurrContent={
                    salesLensItemMetricCardData?.netSalesPercentage
                  }
                  mtdPrevContent={
                    trendData?.prevMetricsMonthly?.netSalesPercentage
                  }
                  ytdPrevContent={
                    trendData?.prevMetricsYearly?.netSalesPercentage
                  }
                  icon={<IconCurrencyDollar color="white" size={20} />}
                  color={metricCardColors.orange500}
                  loading={salesLensItemMetricCardLoading}
                />
                <MetricCard
                  title={t('clarityLens.salesLens.metrics.unitSold')}
                  type="number"
                  content={salesLensItemMetricCardData?.unitSold || 0}
                  mtdCurrContent={salesLensItemMetricCardData?.unitSold}
                  ytdCurrContent={salesLensItemMetricCardData?.unitSold}
                  mtdPrevContent={trendData?.prevMetricsMonthly?.unitSold}
                  ytdPrevContent={trendData?.prevMetricsYearly?.unitSold}
                  icon={<IconBox color="white" size={20} />}
                  color={metricCardColors.green500}
                  loading={salesLensItemMetricCardLoading}
                />
                <MetricCard
                  title={t('clarityLens.salesLens.metrics.avgSellingPrice')}
                  type="money"
                  content={salesLensItemMetricCardData?.avgSellingPrice || 0}
                  mtdCurrContent={salesLensItemMetricCardData?.avgSellingPrice}
                  ytdCurrContent={salesLensItemMetricCardData?.avgSellingPrice}
                  mtdPrevContent={
                    trendData?.prevMetricsMonthly?.avgSellingPrice
                  }
                  ytdPrevContent={trendData?.prevMetricsYearly?.avgSellingPrice}
                  icon={<IconBasketDiscount color="white" size={20} />}
                  color={metricCardColors.red500}
                  loading={salesLensItemMetricCardLoading}
                />
                <MetricCard
                  title={t('clarityLens.salesLens.metrics.discountsPercent')}
                  type="percentage"
                  content={
                    salesLensItemMetricCardData?.netToDiscountPercentage || 0
                  }
                  mtdCurrContent={
                    salesLensItemMetricCardData?.netToDiscountPercentage
                  }
                  ytdCurrContent={
                    salesLensItemMetricCardData?.netToDiscountPercentage
                  }
                  mtdPrevContent={
                    trendData?.prevMetricsMonthly?.netToDiscountPercentage
                  }
                  ytdPrevContent={
                    trendData?.prevMetricsYearly?.netToDiscountPercentage
                  }
                  icon={<IconBasketDiscount color="white" size={20} />}
                  color={metricCardColors.purple500}
                  loading={salesLensItemMetricCardLoading}
                />
              </ScalrCarousel>
            </div>

            <Tabs defaultValue="sales" className="w-full">
              <TabsList className="w-full">
                {tabs.map((tab) => tab.trigger)}
              </TabsList>

              {tabs.map((tab) => tab.content)}
            </Tabs>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const SalesLensPage = () => {
  return (
    <>
      <SalesLensContent />
    </>
  )
}

export default SalesLensPage
